import { initialsFor } from '../lib/format'
import { useMediaUrl } from '../hooks/useMediaUrl'

type AvatarProps = {
  name: string
  /** `/api/media/<id>/thumb` or null; bearer-authenticated, so it loads as an object URL. */
  path?: string | null
  size?: number
  /** Omit to hide the lamp; the textual presence lives next to the name. */
  online?: boolean
  className?: string
}

/**
 * Presence is the family's lamp, not a status LED: lit brass with a glow, or an unlit ring. Fill
 * and glow carry the state alongside the colour, so red-green colour vision deficiency — which is
 * what a green/red dot pair actually depends on — is not what decides whether someone is there.
 */
export function Avatar({ name, path = null, size = 44, online, className = '' }: AvatarProps) {
  const { url } = useMediaUrl(path)
  const dot = Math.max(11, Math.round(size * 0.27))
  return (
    <span className={`relative inline-flex shrink-0 ${className}`} style={{ width: size, height: size }}>
      {url ? (
        <img
          src={url}
          alt=""
          className="h-full w-full rounded-full object-cover ring-1 ring-line-strong ring-inset"
        />
      ) : (
        <span
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center rounded-full bg-surface2 font-semibold text-muted ring-1 ring-line ring-inset"
          style={{ fontSize: Math.max(11, Math.round(size * 0.36)) }}
        >
          {initialsFor(name)}
        </span>
      )}
      {online !== undefined && (
        <span
          aria-hidden="true"
          className={`absolute right-0 bottom-0 rounded-full ring-2 ring-surface ${online ? 'lamp-lit' : 'lamp-off'}`}
          style={{ width: dot, height: dot }}
        />
      )}
    </span>
  )
}
